import { shuffle } from '../utils'

// ── Types ──────────────────────────────────────────────────────

export type PillShape  = 'circle' | 'oval' | 'capsule' | 'tablet'
export type Schedule   = 'breakfast' | 'lunch' | 'merienda' | 'dinner'
export type Difficulty = 'easy' | 'medium' | 'hard'

export const SCHEDULES: Schedule[] = ['breakfast', 'lunch', 'merienda', 'dinner']

export interface Medicine {
  id:        string
  name:      string
  pillShape: PillShape
  pillColor: string
  dosage:    Record<Schedule, number>   // 0 = no pill at this time
}

export interface Bottle {
  id:           string
  medicineId:   string   // "distractor-N" for bottles not in the guideline
  name:         string
  pillShape:    PillShape
  pillColor:    string
  isDistractor: boolean
}

export interface PlacedPill {
  id:         string
  medicineId: string
  pillShape:  PillShape
  pillColor:  string
}

// ── Static data ────────────────────────────────────────────────

const MEDICINE_CATALOG = [
  'Lorazil',  'Carditex', 'Meprofix', 'Diabazol',  'Tensoderm',
  'Glucamin', 'Norvapin', 'Artridex', 'Vitamex',   'Calmovit',
  'Omepraz',  'Metformax','Atenokard','Ibuprofex',  'Simvaderm',
  'Prostatex','Osteokin', 'Amlodipin','Lisinoplex', 'Topiratex',
]

const PILL_SHAPES: PillShape[] = ['circle', 'oval', 'capsule', 'tablet']

const PILL_COLORS = [
  '#E63946', // red
  '#4A90D9', // blue
  '#27AE60', // green
  '#F39C12', // orange
  '#8E44AD', // purple
  '#E91E8C', // pink
  '#F1C40F', // yellow
  '#1ABC9C', // teal
  '#E67E22', // amber
  '#2C3E50', // dark blue
]

// ── Difficulty config ──────────────────────────────────────────

interface DifficultyConfig {
  medicineCount:   number
  distractorCount: number
  maxDosage:       number
  minActiveSched:  number
}

const DIFFICULTY_CONFIG: Record<Difficulty, DifficultyConfig> = {
  easy:   { medicineCount: 2, distractorCount: 0, maxDosage: 1, minActiveSched: 2 },
  medium: { medicineCount: 3, distractorCount: 1, maxDosage: 2, minActiveSched: 2 },
  hard:   { medicineCount: 4, distractorCount: 2, maxDosage: 3, minActiveSched: 3 },
}

// ── Generation helpers ─────────────────────────────────────────

function randInt(min: number, max: number): number {
  return min + Math.floor(Math.random() * (max - min + 1))
}

function generateDosage(maxDosage: number, minActive: number): Record<Schedule, number> {
  const activeCount = randInt(minActive, SCHEDULES.length)
  const active      = new Set(shuffle([...SCHEDULES]).slice(0, activeCount))
  return {
    breakfast: active.has('breakfast') ? randInt(1, maxDosage) : 0,
    lunch:     active.has('lunch')     ? randInt(1, maxDosage) : 0,
    merienda:  active.has('merienda')  ? randInt(1, maxDosage) : 0,
    dinner:    active.has('dinner')    ? randInt(1, maxDosage) : 0,
  }
}

// ── Public generators ──────────────────────────────────────────

export function generateGame(difficulty: Difficulty): { medicines: Medicine[]; bottles: Bottle[] } {
  const { medicineCount, distractorCount, maxDosage, minActiveSched } = DIFFICULTY_CONFIG[difficulty]
  const total = medicineCount + distractorCount

  const names  = shuffle([...MEDICINE_CATALOG]).slice(0, total)
  // Repeat shapes so we always have enough even when total > 4
  const shapes = shuffle([...PILL_SHAPES, ...PILL_SHAPES, ...PILL_SHAPES]).slice(0, total)
  const colors = shuffle([...PILL_COLORS]).slice(0, total)

  const medicines: Medicine[] = names.slice(0, medicineCount).map((name, i) => ({
    id:        `med-${i}`,
    name,
    pillShape: shapes[i],
    pillColor: colors[i],
    dosage:    generateDosage(maxDosage, minActiveSched),
  }))

  const guidBottles: Bottle[] = medicines.map((med) => ({
    id:           `bottle-${med.id}`,
    medicineId:   med.id,
    name:         med.name,
    pillShape:    med.pillShape,
    pillColor:    med.pillColor,
    isDistractor: false,
  }))

  const distractors: Bottle[] = names.slice(medicineCount).map((name, i) => ({
    id:           `bottle-distractor-${i}`,
    medicineId:   `distractor-${i}`,
    name,
    pillShape:    shapes[medicineCount + i],
    pillColor:    colors[medicineCount + i],
    isDistractor: true,
  }))

  return { medicines, bottles: shuffle([...guidBottles, ...distractors]) }
}

export function emptyCompartments(): Record<Schedule, PlacedPill[]> {
  return { breakfast: [], lunch: [], merienda: [], dinner: [] }
}

export function checkResult(
  medicines:    Medicine[],
  compartments: Record<Schedule, PlacedPill[]>,
): Schedule[] {
  const wrong       = new Set<Schedule>()
  const medicineIds = new Set(medicines.map(m => m.id))

  for (const schedule of SCHEDULES) {
    const placed = compartments[schedule]

    // Any distractor pill is wrong
    if (placed.some(p => !medicineIds.has(p.medicineId))) {
      wrong.add(schedule)
      continue
    }

    // Check each medicine's count for this schedule
    for (const med of medicines) {
      const count = placed.filter(p => p.medicineId === med.id).length
      if (count !== med.dosage[schedule]) { wrong.add(schedule); break }
    }
  }

  return [...wrong]
}

/** Total pills the user must place across all compartments */
export function totalRequiredPills(medicines: Medicine[]): number {
  return medicines.reduce((sum, med) =>
    sum + SCHEDULES.reduce((s, sch) => s + med.dosage[sch], 0), 0)
}
